import React, { useEffect, useState, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { dateTimeToMonDDYYYYWithTime } from '../utils/dateUtils';
import LoadingSpinner from '../components/LoadingSpinner';

/** Compact neutral chips (Community page + modals) */
const uiFilterChip = (active) => ({
  padding: '6px 11px',
  fontSize: '12px',
  fontWeight: active ? 600 : 500,
  borderRadius: '6px',
  cursor: 'pointer',
  border: `1px solid ${active ? '#cbd5e1' : '#e2e8f0'}`,
  backgroundColor: active ? '#f1f5f9' : '#ffffff',
  color: active ? '#0f172a' : '#475569',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
});

const uiSectionLabel = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#94a3b8',
  margin: '0 0 8px 0',
};

const uiModalOverlay = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.4)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 16,
};

const uiModalPanel = {
  backgroundColor: '#ffffff',
  borderRadius: 12,
  padding: '18px 20px',
  maxWidth: 560,
  width: '100%',
  maxHeight: 'min(88vh, 720px)',
  overflowY: 'auto',
  boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)',
  border: '1px solid #e2e8f0',
  position: 'relative',
};

const uiField = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '13px',
  boxSizing: 'border-box',
  backgroundColor: '#fff',
};

const uiCloseBtn = {
  position: 'absolute',
  top: 10,
  right: 10,
  background: 'transparent',
  border: 'none',
  fontSize: '22px',
  lineHeight: 1,
  cursor: 'pointer',
  color: '#94a3b8',
  padding: '6px 8px',
  borderRadius: '8px',
};

// Create User Modal Component
const CreateUserModal = ({
  isOpen,
  onClose,
  schools,
  userPermissions,
  periods,
  associations,
  concentrations,
  canAddStudentEnrollment = false,
}) => {
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedRole, setSelectedRole] = useState('Student');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [studentForm, setStudentForm] = useState({
    first_name: '',
    last_name: '',
    primary_email: '',
    password: '',
    gradelevel: '',
    association_id: '',
    period_id: ''
  });
  const [selectedConcentration, setSelectedConcentration] = useState(null);
  const [loading, setLoading] = useState(false);

  // Helper function to convert grade number to proper label
  const formatGradeLevel = (grade) => {
    if (!grade) return null;
    if (grade === 'TK') return 'TK';
    const num = parseInt(grade);
    if (isNaN(num)) return grade;
    
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  };

  // Set default values when modal opens using props
  useEffect(() => {
    if (isOpen && periods.length > 0) {
      console.log('Setting default period. Available periods:', periods);
      console.log('Current selectedPeriod:', selectedPeriod);
      
      // Try to find 2025-2026 first
      const period2025_2026 = periods.find(p => p.name === '2025-2026');
      console.log('Found 2025-2026 period:', period2025_2026);
      
      if (period2025_2026) {
        console.log('Setting selectedPeriod to 2025-2026:', period2025_2026);
        setSelectedPeriod(period2025_2026);
      } else {
        // If no 2025-2026, find the period with the latest start_date
        const sortedPeriods = [...periods].sort((a, b) => {
          const dateA = new Date(a.start_date);
          const dateB = new Date(b.start_date);
          return dateB - dateA; // Most recent first
        });
        console.log('No 2025-2026 found, using latest period by start_date:', sortedPeriods[0]);
        setSelectedPeriod(sortedPeriods[0]);
      }
    }
  }, [isOpen, periods]);

  // Reset selections when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPeriod(null);
      setSelectedAssociation(null);
      setSelectedConcentration(null);
      setStudentForm({
        first_name: '',
        last_name: '',
        primary_email: '',
        password: '',
        gradelevel: '',
        association_id: '',
        period_id: ''
      });
    }
  }, [isOpen]);

  // Set default school when modal opens
  useEffect(() => {
    if (isOpen && schools.length > 0 && !selectedSchool) {
      // First, try to find "Scale Leadership Academy East"
      const scaleAcademyEast = schools.find(school => 
        school.school_name === "Scale Leadership Academy East"
      );
      
      if (scaleAcademyEast) {
        setSelectedSchool(scaleAcademyEast);
      } else if (userPermissions?.isGlobal) {
        setSelectedSchool(schools[0]);
      } else {
        const userSchool = schools.find(school => school.school_id === userPermissions?.school_id);
        setSelectedSchool(userSchool || schools[0]);
      }
    }
  }, [isOpen, schools, selectedSchool, userPermissions]);

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    if (!selectedSchool) return;
    if (canAddStudentEnrollment && (!selectedPeriod || !selectedAssociation)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const enrollmentPayload =
        canAddStudentEnrollment && selectedPeriod && selectedAssociation
          ? {
              start_date: selectedPeriod.start_date || '2025-07-01',
              end_date: selectedPeriod.end_date || '2026-06-30',
              gradelevel: formatGradeLevel(studentForm.gradelevel) || null,
              concentration: selectedConcentration?.id || null,
              association: selectedAssociation.id,
              period_id: selectedPeriod.period_id || null,
            }
          : undefined;

      const response = await axios.post(
        '/api/user-management/account-with-accesscard',
        {
          account: {
            first_name: studentForm.first_name,
            last_name: studentForm.last_name,
            primary_email: studentForm.primary_email,
            password: studentForm.password,
          },
          accesscard: {
            email: studentForm.primary_email,
            role: 'Student',
            global: false,
            school_id: selectedSchool.school_id,
          },
          ...(enrollmentPayload ? { enrollment: enrollmentPayload } : {}),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.account) {
        // Reset form
        setStudentForm({
          first_name: '',
          last_name: '',
          primary_email: '',
          gradelevel: '',
          association_id: '',
          period_id: ''
        });
        onClose();
        // You might want to refresh the community data here
        window.location.reload(); // Simple refresh for now
      }
    } catch (error) {
      console.error('Error creating student:', error);
      alert('Failed to create student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={uiModalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="community-create-user-title"
        style={uiModalPanel}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={uiCloseBtn}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.color = '#475569';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#94a3b8';
          }}
        >
          ×
        </button>

        <h2
          id="community-create-user-title"
          style={{
            margin: '0 0 4px 0',
            color: '#0f172a',
            fontSize: '17px',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            paddingRight: 36,
          }}
        >
          Create new user
        </h2>
        <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '13px', lineHeight: 1.45, paddingRight: 36 }}>
          {canAddStudentEnrollment
            ? 'Student account, access card, and enrollment in one step.'
            : 'Student account and access card. Add enrollments separately if you have that permission.'}
        </p>

        <div style={{ marginBottom: 14 }}>
          <p style={uiSectionLabel}>School</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {schools.map((school) => {
              const on = selectedSchool?.school_id === school.school_id;
              return (
                <button
                  key={school.school_id}
                  type="button"
                  onClick={() => setSelectedSchool(school)}
                  style={{
                    ...uiFilterChip(on),
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    maxWidth: '100%',
                    textAlign: 'left',
                  }}
                >
                  <img
                    src={
                      school.logo_url ||
                      'https://acuitycte-files.s3.us-west-2.amazonaws.com/public/acuity-tech-logo.png'
                    }
                    alt=""
                    style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
                  />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {school.school_name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <p style={uiSectionLabel}>Role</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setSelectedRole('Student')}
              style={uiFilterChip(selectedRole === 'Student')}
            >
              Student
            </button>
          </div>
        </div>

        {canAddStudentEnrollment && selectedRole === 'Student' && periods.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <p style={uiSectionLabel}>Term</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {periods.map((period, index) => {
                const isSelected = selectedPeriod && selectedPeriod.name === period.name;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedPeriod(period)}
                    style={uiFilterChip(isSelected)}
                  >
                    {period.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {canAddStudentEnrollment && selectedRole === 'Student' && associations.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <p style={uiSectionLabel}>Association (FA)</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {associations.map((association) => (
                <button
                  key={association.id}
                  type="button"
                  onClick={() => setSelectedAssociation(association)}
                  style={uiFilterChip(selectedAssociation?.id === association.id)}
                >
                  {association.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedRole === 'Student' &&
          selectedSchool &&
          (canAddStudentEnrollment ? selectedPeriod && selectedAssociation : true) && (
          <form onSubmit={handleCreateStudent}>
            <p style={{ ...uiSectionLabel, marginTop: 4 }}>Student details</p>
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                    First name
                  </label>
                  <input
                    type="text"
                    value={studentForm.first_name}
                    onChange={(e) => setStudentForm({ ...studentForm, first_name: e.target.value })}
                    required
                    style={uiField}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                    Last name
                  </label>
                  <input
                    type="text"
                    value={studentForm.last_name}
                    onChange={(e) => setStudentForm({ ...studentForm, last_name: e.target.value })}
                    required
                    style={uiField}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                  Email
                </label>
                <input
                  type="email"
                  value={studentForm.primary_email}
                  onChange={(e) => setStudentForm({ ...studentForm, primary_email: e.target.value })}
                  required
                  style={uiField}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                  Password
                </label>
                <input
                  type="password"
                  value={studentForm.password}
                  onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
                  required
                  style={uiField}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                  Grade
                </label>
                <select
                  value={studentForm.gradelevel}
                  onChange={(e) => setStudentForm({ ...studentForm, gradelevel: e.target.value })}
                  required={canAddStudentEnrollment}
                  style={uiField}
                >
                  <option value="">Select grade</option>
                  <option value="TK">TK</option>
                  <option value="1">1st</option>
                  <option value="2">2nd</option>
                  <option value="3">3rd</option>
                  <option value="4">4th</option>
                  <option value="5">5th</option>
                  <option value="6">6th</option>
                  <option value="7">7th</option>
                  <option value="8">8th</option>
                  <option value="9">9th</option>
                  <option value="10">10th</option>
                  <option value="11">11th</option>
                  <option value="12">12th</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                  Concentration
                </label>
                <select
                  value={selectedConcentration?.id || ''}
                  onChange={(e) => {
                    const concentration = concentrations.find((c) => c.id === e.target.value);
                    setSelectedConcentration(concentration);
                  }}
                  style={uiField}
                >
                  <option value="">Optional</option>
                  {concentrations.map((concentration) => (
                    <option key={concentration.id} value={concentration.id}>
                      {concentration.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#fff',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '8px 14px',
                  backgroundColor: loading ? '#cbd5e1' : '#334155',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Creating…' : 'Create student'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Add Enrollment Modal Component
const AddEnrollmentModal = ({ isOpen, onClose, accesscard, periods, associations, concentrations, onEnrollmentAdded }) => {
  const [enrollmentForm, setEnrollmentForm] = useState({
    start_date: '',
    end_date: '',
    gradelevel: '',
    concentration: '',
    association: '',
    period_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to convert grade number to proper label
  const formatGradeLevel = (grade) => {
    if (!grade) return null;
    if (grade === 'TK') return 'TK';
    const num = parseInt(grade);
    if (isNaN(num)) return grade;
    
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setEnrollmentForm({
        start_date: '',
        end_date: '',
        gradelevel: '',
        concentration: '',
        association: '',
        period_id: ''
      });
      setError(null);
    }
  }, [isOpen]);

  // Auto-fill dates when term is selected
  useEffect(() => {
    if (enrollmentForm.period_id && periods.length > 0) {
      const selectedPeriod = periods.find(p => 
        p.period_id === enrollmentForm.period_id
      );
      
      if (selectedPeriod) {
        console.log('Selected period for AddEnrollment:', selectedPeriod);
        console.log('Available fields:', Object.keys(selectedPeriod));
        
        // Use the actual selected period's dates, not fallback dates
        const startDate = selectedPeriod.start_date;
        const endDate = selectedPeriod.end_date;
        
        console.log('Using selected period start_date:', startDate);
        console.log('Using selected period end_date:', endDate);
        
        // Format dates for HTML date input (YYYY-MM-DD)
        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        };
        
        const formattedStartDate = formatDateForInput(startDate);
        const formattedEndDate = formatDateForInput(endDate);
        
        console.log('Formatted start_date:', formattedStartDate);
        console.log('Formatted end_date:', formattedEndDate);
        
        setEnrollmentForm(prev => ({
          ...prev,
          start_date: formattedStartDate,
          end_date: formattedEndDate
        }));
      }
    }
  }, [enrollmentForm.period_id, periods]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accesscard) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post('/api/user-management/enrollment', {
        accesscard_id: accesscard.accesscard_id,
        start_date: enrollmentForm.start_date,
        end_date: enrollmentForm.end_date,
        gradelevel: formatGradeLevel(enrollmentForm.gradelevel) || null,
        concentration: enrollmentForm.concentration || null,
        association: enrollmentForm.association || null,
        period_id: enrollmentForm.period_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.enrollment) {
        // Call the callback to refresh the data
        if (onEnrollmentAdded) {
          onEnrollmentAdded(response.data.enrollment);
        }
        onClose();
      }
    } catch (err) {
      console.error('Error creating enrollment:', err);
      setError(err.response?.data?.error || 'Failed to create enrollment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={uiModalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="community-add-enrollment-title"
        style={uiModalPanel}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={uiCloseBtn}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.color = '#475569';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#94a3b8';
          }}
        >
          ×
        </button>

        <div style={{ marginBottom: 14, paddingRight: 36 }}>
          <h2
            id="community-add-enrollment-title"
            style={{
              margin: 0,
              color: '#0f172a',
              fontSize: '17px',
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            Add enrollment
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px', wordBreak: 'break-word' }}>
            {accesscard?.email}
          </p>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              padding: '10px 12px',
              borderRadius: '8px',
              marginBottom: 12,
              fontSize: '13px',
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                Term
              </label>
              <select
                value={enrollmentForm.period_id}
                onChange={(e) => setEnrollmentForm({ ...enrollmentForm, period_id: e.target.value })}
                style={uiField}
              >
                <option value="">Select Term</option>
                {periods.map(period => (
                  <option key={period.period_id} value={period.period_id}>
                    {period.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                  Start
                </label>
                <input
                  type="date"
                  value={enrollmentForm.start_date}
                  onChange={(e) => setEnrollmentForm({ ...enrollmentForm, start_date: e.target.value })}
                  required
                  style={uiField}
                />
                {enrollmentForm.period_id && (
                  <small style={{ color: '#94a3b8', fontSize: '11px', marginTop: 4, display: 'block' }}>
                    From term
                  </small>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                  End
                </label>
                <input
                  type="date"
                  value={enrollmentForm.end_date}
                  onChange={(e) => setEnrollmentForm({ ...enrollmentForm, end_date: e.target.value })}
                  style={uiField}
                />
                {enrollmentForm.period_id && (
                  <small style={{ color: '#94a3b8', fontSize: '11px', marginTop: 4, display: 'block' }}>
                    From term
                  </small>
                )}
              </div>
            </div>

            <div>
              <p style={uiSectionLabel}>Grade</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['TK', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map((grade) => (
                  <button
                    key={grade}
                    type="button"
                    onClick={() => setEnrollmentForm({ ...enrollmentForm, gradelevel: grade })}
                    style={{
                      ...uiFilterChip(enrollmentForm.gradelevel === grade),
                      minWidth: 36,
                      textAlign: 'center',
                      padding: '5px 8px',
                    }}
                  >
                    {grade === 'TK'
                      ? 'TK'
                      : `${grade}${grade === '1' ? 'st' : grade === '2' ? 'nd' : grade === '3' ? 'rd' : 'th'}`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p style={uiSectionLabel}>Association (FA)</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {associations.map((association) => (
                  <button
                    key={association.id}
                    type="button"
                    onClick={() => setEnrollmentForm({ ...enrollmentForm, association: association.id })}
                    style={{ ...uiFilterChip(enrollmentForm.association === association.id), whiteSpace: 'nowrap' }}
                  >
                    {association.title}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p style={uiSectionLabel}>Concentration</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {concentrations.map((concentration) => (
                  <button
                    key={concentration.id}
                    type="button"
                    onClick={() => setEnrollmentForm({ ...enrollmentForm, concentration: concentration.id })}
                    style={{ ...uiFilterChip(enrollmentForm.concentration === concentration.id), whiteSpace: 'nowrap' }}
                  >
                    {concentration.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 14px',
                backgroundColor: '#fff',
                color: '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '8px 14px',
                backgroundColor: loading ? '#cbd5e1' : '#334155',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              {loading ? 'Creating…' : 'Create enrollment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ROLE_TOOLTIP_Z = 12000;
const ROLE_TOOLTIP_MAX_W = 320;
const ROLE_TOOLTIP_GAP = 8;
const ROLE_TOOLTIP_MARGIN = 12;

/**
 * Hover tooltip rendered via portal + fixed positioning (same idea as CourseSubjectManagerModal
 * create popover) so it is never clipped by modal/scroll overflow.
 */
const RoleTooltip = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState(null);
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const rafRef = useRef(null);

  const defaultWidth =
    typeof window !== 'undefined'
      ? Math.min(ROLE_TOOLTIP_MAX_W, window.innerWidth - ROLE_TOOLTIP_MARGIN * 2)
      : ROLE_TOOLTIP_MAX_W;

  const computePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const tip = tooltipRef.current;
    if (!anchor || !tip) return;

    const rect = anchor.getBoundingClientRect();
    const margin = ROLE_TOOLTIP_MARGIN;
    const gap = ROLE_TOOLTIP_GAP;
    const w = Math.min(ROLE_TOOLTIP_MAX_W, window.innerWidth - margin * 2);
    let left = rect.left + rect.width / 2 - w / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - w - margin));

    const h = tip.offsetHeight;
    const spaceAbove = rect.top - margin;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    let top;
    let placement;

    if (spaceAbove >= h + gap || spaceAbove >= spaceBelow) {
      top = rect.top - gap - h;
      if (top < margin) top = margin;
      placement = 'above';
    } else {
      top = rect.bottom + gap;
      if (top + h > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - margin - h);
      }
      placement = 'below';
    }

    setCoords({ top, left, width: w, placement });
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) {
      setCoords(null);
      return undefined;
    }

    computePosition();
    rafRef.current = requestAnimationFrame(() => computePosition());

    const onReposition = () => computePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => computePosition());
      if (tooltipRef.current) ro.observe(tooltipRef.current);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      ro?.disconnect();
    };
  }, [isVisible, content, computePosition]);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);

  const tooltipNode =
    isVisible &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          top: coords ? coords.top : -9999,
          left: coords ? coords.left : 0,
          width: coords ? coords.width : defaultWidth,
          visibility: coords ? 'visible' : 'hidden',
          backgroundColor: '#1D4E89',
          color: 'white',
          padding: '0.75rem 1rem',
          borderRadius: '6px',
          fontSize: '0.875rem',
          minWidth: '200px',
          boxSizing: 'border-box',
          zIndex: ROLE_TOOLTIP_Z,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: '1.4',
          border: '2px solid #fff',
          pointerEvents: 'none',
          opacity: coords ? 1 : 0,
          transition: coords ? 'opacity 0.15s ease-out' : undefined,
        }}
      >
        {coords?.placement === 'above' && (
          <div
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              border: '6px solid transparent',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              borderTopColor: '#1D4E89',
            }}
          />
        )}
        {coords?.placement === 'below' && (
          <div
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              border: '6px solid transparent',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              borderBottomColor: '#1D4E89',
            }}
          />
        )}
        <div style={{ marginBottom: '0.25rem' }}>{content}</div>
      </div>,
      document.body
    );

  return (
    <div
      ref={anchorRef}
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {tooltipNode}
    </div>
  );
};

/** Enrollment context for teacher-of-record chips (matches RoleTooltip styling via parent). */
const TorStudentEnrollmentTooltipContent = ({ student }) => {
  const line = (label, val) => {
    if (val == null || val === '') return null;
    return (
      <div style={{ marginBottom: '0.35rem' }}>
        <span style={{ fontWeight: 600, opacity: 0.95 }}>{label}</span> {val}
      </div>
    );
  };
  const formatDate = (d) => (d ? dateTimeToMonDDYYYYWithTime(d, false) : null);
  const hasAny =
    student.school_name ||
    student.period_name ||
    student.association_title ||
    student.concentration_title ||
    student.gradelevel != null ||
    student.start_date ||
    student.end_date;
  if (!hasAny) {
    return <div style={{ opacity: 0.9 }}>No enrollment details available.</div>;
  }
  const dateRange =
    student.start_date && student.end_date
      ? `${formatDate(student.start_date)} → ${formatDate(student.end_date)}`
      : formatDate(student.start_date) || formatDate(student.end_date);
  return (
    <>
      {line('School:', student.school_name)}
      {line('Term:', student.period_name)}
      {line('Association:', student.association_title)}
      {line('Concentration:', student.concentration_title)}
      {line('Grade:', student.gradelevel != null ? String(student.gradelevel) : null)}
      {dateRange ? (
        <div style={{ marginTop: '0.15rem', fontSize: '0.8125rem', opacity: 0.92 }}>{dateRange}</div>
      ) : null}
    </>
  );
};

// Account Details Modal Component
const AccountDetailsModal = ({
  isOpen,
  onClose,
  account,
  torStudents = {},
  onAddEnrollment,
  canAddStudentEnrollment = false,
}) => {
  if (!isOpen) return null;

  /** TOR / teacher-of-record students are keyed by account_id, not access card */
  const accountTorStudents = torStudents[account.account_id] || [];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="community-account-modal-title"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '18px 20px',
          maxWidth: '520px',
          width: '100%',
          maxHeight: 'min(88vh, 720px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)',
          border: '1px solid #e5e7eb',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'transparent',
            border: 'none',
            fontSize: '22px',
            lineHeight: 1,
            cursor: 'pointer',
            color: '#94a3b8',
            padding: '6px 8px',
            borderRadius: '8px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.color = '#475569';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#94a3b8';
          }}
        >
          ×
        </button>

        <div style={{ flexShrink: 0 }}>
        {/* Account identity + account-level TOR (outside scroll so hover tooltips are not clipped) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '16px',
            paddingRight: '36px',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#f8fafc',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: '1px solid #e2e8f0',
              fontSize: '15px',
              fontWeight: 600,
              color: '#64748b',
            }}
          >
            {account.first_name?.[0]}
            {account.last_name?.[0]}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2
              id="community-account-modal-title"
              style={{
                margin: 0,
                color: '#0f172a',
                fontSize: '17px',
                fontWeight: 600,
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
              }}
            >
              {account.first_name} {account.last_name}
            </h2>
            <p
              style={{
                margin: '4px 0 0 0',
                color: '#64748b',
                fontSize: '13px',
                wordBreak: 'break-word',
              }}
            >
              {account.primary_email}
            </p>
            {account.dob && (
              <p style={{ margin: '4px 0 0 0', color: '#94a3b8', fontSize: '12px' }}>
                DOB {new Date(account.dob).toLocaleDateString()}
              </p>
            )}

            {accountTorStudents.length > 0 && (
              <div
                style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: '#64748b',
                    marginBottom: '8px',
                  }}
                >
                  Teacher of record · {accountTorStudents.length}{' '}
                  {accountTorStudents.length === 1 ? 'student' : 'students'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {accountTorStudents.map((student, idx) => (
                    <RoleTooltip
                      key={student.enrollment_id || idx}
                      content={<TorStudentEnrollmentTooltipContent student={student} />}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#334155',
                          fontWeight: 500,
                          cursor: 'default',
                        }}
                      >
                        {student.first_name && student.last_name
                          ? `${student.first_name} ${student.last_name}`
                          : `Student ${idx + 1}`}
                      </span>
                    </RoleTooltip>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Access cards (per school / role) — scrollable */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingTop: '2px' }}>
          <div
            style={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: '#94a3b8',
              marginBottom: '10px',
            }}
          >
            Access cards · {account.accesscards?.length || 0}
          </div>

          {account.accesscards && account.accesscards.length > 0 ? (
            account.accesscards.map((card, index) => (
              <AccessCardDisplay
                key={card.accesscard_id || index}
                card={card}
                onAddEnrollment={onAddEnrollment}
                canAddStudentEnrollment={canAddStudentEnrollment}
              />
            ))
          ) : (
            <div
              style={{
                padding: '20px 14px',
                textAlign: 'center',
                color: '#64748b',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px dashed #cbd5e1',
                fontSize: '13px',
              }}
            >
              No access cards for this account.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Community = ({ userData }) => {
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]); // Store original data for global users
  const [schools, setSchools] = useState([]);
  const [studentAssociations, setStudentAssociations] = useState([]);
  const [studentPeriods, setStudentPeriods] = useState([]);
  const [instructorTORs, setInstructorTORs] = useState([]);
  const [torStudents, setTorStudents] = useState({});
  const [userPermissions, setUserPermissions] = useState(null);
  const [concentrations, setConcentrations] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [selectedAssociation, setSelectedAssociation] = useState('All Associations');
  const [selectedPeriod, setSelectedPeriod] = useState('All Terms');
  const [selectedTOR, setSelectedTOR] = useState('All TORs');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isAddEnrollmentModalOpen, setIsAddEnrollmentModalOpen] = useState(false);
  const [selectedAccessCard, setSelectedAccessCard] = useState(null);
  const [communityCapabilities, setCommunityCapabilities] = useState({
    CreateUser: false,
    AddStudentEnrollment: false,
  });

  const ROLES = ['Student', 'Instructor', 'Coordinator', 'Admin', 'Super - Admin', 'Registrar'];
  
  const ROLE_DESCRIPTIONS = {
    'Student': 'May register for events, complete Directory projects, submit hour waiver forms, and explore various modules through structured coursework',
    'Instructor': 'May propose events, submit training plans & register students for events, take attendance for events, create assignments & design modules, and grade student submissions',
    'Coordinator': 'Review events and training plans',
    'Admin': 'Instructor and Coordinator powers + second highest level for Waiver Forms',
    'Super - Admin': 'Instructor and Coordinator powers + Highest level for Waiver Forms',
    'Registrar': 'May register students for events'
  };

  // Helper function to apply all filters
  const applyFilters = (baseAccounts, roleFilter, associationFilter, periodFilter, torFilter) => {
    let filtered = baseAccounts;

    // Apply role filter
    if (roleFilter !== 'All Roles') {
      filtered = filtered.filter(account =>
        account.accesscards.some(card => card.role === roleFilter)
      );
    }

    // Apply association filter (only for students)
    if (roleFilter === 'Student' && associationFilter !== 'All Associations') {
      filtered = filtered.filter(account =>
        account.accesscards.some(card => 
          card.role === 'Student' && 
          card.enrollments && 
          card.enrollments.some(enrollment => 
            enrollment.association_id === associationFilter
          )
        )
      );
    }

    // Apply period filter (only for students)
    if (roleFilter === 'Student' && periodFilter !== 'All Terms') {
      filtered = filtered.filter(account =>
        account.accesscards.some(card => 
          card.role === 'Student' && 
          card.enrollments && 
          card.enrollments.some(enrollment => 
            enrollment.period_id === periodFilter
          )
        )
      );
    }

    // Apply TOR filter (only for instructors)
    if (roleFilter === 'Instructor' && torFilter !== 'All TORs') {
      if (torFilter === 'Regular') {
        // Show only instructors who are NOT TORs
        filtered = filtered.filter(account =>
          account.accesscards.some(card => 
            card.role === 'Instructor' && 
            !instructorTORs.some(tor => tor.account_id === account.account_id)
          )
        );
      } else if (torFilter === 'TOR') {
        // Show only instructors who ARE TORs
        filtered = filtered.filter(account =>
          account.accesscards.some(card => 
            card.role === 'Instructor' && 
            instructorTORs.some(tor => tor.account_id === account.account_id)
          )
        );
      }
    }

    // Sort by first name, then last name
    return filtered.sort((a, b) => {
      if (a.first_name !== b.first_name) {
        return a.first_name.localeCompare(b.first_name);
      }
      return a.last_name.localeCompare(b.last_name);
    });
  };

  useEffect(() => {
    fetchCommunityData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/check-permission?pagePath=community', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        setCommunityCapabilities({
          CreateUser: Boolean(res.data.capabilities?.CreateUser),
          AddStudentEnrollment: Boolean(res.data.capabilities?.AddStudentEnrollment),
        });
      } catch {
        if (!cancelled) {
          setCommunityCapabilities({ CreateUser: false, AddStudentEnrollment: false });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.account_id]);

  const fetchCommunityData = async (schoolId = null) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = schoolId ? { school_id: schoolId } : {};
      
      const response = await axios.get('/api/user-management/community', { 
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });
      
      const accountsData = response.data.accounts || [];
      setAccounts(accountsData);
      setAllAccounts(accountsData); // Store original data for global users
      setSchools(response.data.schools || []);
      setStudentAssociations(response.data.studentAssociations || []);
      setStudentPeriods(response.data.studentPeriods || []);
      setInstructorTORs(response.data.instructorTORs || []);
      setTorStudents(response.data.torStudents || {});
      setUserPermissions(response.data.userPermissions);
      
      // Fetch concentrations
      const concentrationsResponse = await axios.get('/api/concentrations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConcentrations(concentrationsResponse.data.concentrations || []);
      
      // Fetch complete Periods with start_date and end_date
      const periodsResponse = await axios.get('/api/user-management/periods', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentPeriods(periodsResponse.data.periods || []);
      
      // Debug logging
      console.log('Community Debug - torStudents:', response.data.torStudents);
      console.log('Community Debug - instructorTORs:', response.data.instructorTORs);
      
      // Set default selected school based on user's default access card
      if (!selectedSchool) {
        if (response.data.userPermissions.isGlobal && response.data.schools.length > 0) {
          // Global users: don't select any specific school by default (show "All Schools")
          setSelectedSchool(null);
                 } else if (!response.data.userPermissions.isGlobal && response.data.userPermissions.school_id) {
           // Non-global users: find their school in the schools list
           const userSchool = response.data.schools.find(school => school.school_id === response.data.userPermissions.school_id);
           if (userSchool) {
             setSelectedSchool(userSchool);
           } else {
             // If school not found in schools list, create a school object with the data we have
             setSelectedSchool({
               school_id: response.data.userPermissions.school_id,
               school_name: response.data.userPermissions.school_name || 'Your School'
             });
           }
         }
       }
       
       // Set default period to most recent if Student role is selected and periods are available
       if (selectedRole === 'Student' && response.data.studentPeriods && response.data.studentPeriods.length > 0 && selectedPeriod === 'All Terms') {
         const sortedPeriods = [...response.data.studentPeriods].sort((a, b) => {
           // Use start_date if available, otherwise fall back to name
           if (a.start_date && b.start_date) {
             return new Date(b.start_date) - new Date(a.start_date);
           }
           return b.name.localeCompare(a.name);
         });
         setSelectedPeriod(sortedPeriods[0].period_id);
       }
    } catch (err) {
      setError('Failed to load community data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
    // For global users, filter client-side instead of making new API call
    if (userPermissions?.isGlobal) {
      // Filter accounts to show only users from the selected school
      let filteredAccounts = allAccounts.filter(account => 
        account.accesscards.some(card => card.school_id === school.school_id)
      );
      
      const finalFilteredAccounts = applyFilters(filteredAccounts, selectedRole, selectedAssociation, selectedPeriod, selectedTOR);
      setAccounts(finalFilteredAccounts);
    } else {
      // For non-global users, fetch from API
      fetchCommunityData(school.school_id);
    }
  };

  const handleShowAllSchools = () => {
    setSelectedSchool(null);
    // For global users, restore all accounts from the original data
    if (userPermissions?.isGlobal) {
      const filteredAccounts = applyFilters(allAccounts, selectedRole, selectedAssociation, selectedPeriod, selectedTOR);
      setAccounts(filteredAccounts);
    } else {
      fetchCommunityData();
    }
  };

  const handleRoleFilter = (role) => {
    setSelectedRole(role);
    // Reset association, period, and TOR filters when changing roles
    setSelectedAssociation('All Associations');
    setSelectedTOR('All TORs');
    
    // For students, default to most recent period if available
    let defaultPeriod = 'All Terms';
    if (role === 'Student' && studentPeriods.length > 0) {
      // Sort periods by start_date descending to get most recent
      const sortedPeriods = [...studentPeriods].sort((a, b) => {
        // Use start_date if available, otherwise fall back to name
        if (a.start_date && b.start_date) {
          return new Date(b.start_date) - new Date(a.start_date);
        }
        return b.name.localeCompare(a.name);
      });
      defaultPeriod = sortedPeriods[0].period_id;
    }
    setSelectedPeriod(defaultPeriod);
    
    // For both global and non-global users, use the same filtering logic
    if (userPermissions?.isGlobal) {
      // Global users: filter from allAccounts based on school selection
      const baseAccounts = selectedSchool 
        ? allAccounts.filter(account => 
            account.accesscards.some(card => card.school_id === selectedSchool.school_id)
          )
        : allAccounts;
      
      const filteredAccounts = applyFilters(baseAccounts, role, 'All Associations', defaultPeriod, 'All TORs');
      setAccounts(filteredAccounts);
    } else {
      // Non-global users: filter from allAccounts (which contains their school's data)
      const baseAccounts = allAccounts;
      const filteredAccounts = applyFilters(baseAccounts, role, 'All Associations', defaultPeriod, 'All TORs');
      setAccounts(filteredAccounts);
    }
  };

  const handleAssociationFilter = (associationId) => {
    setSelectedAssociation(associationId);
    
    // For both global and non-global users, use the same filtering logic
    if (userPermissions?.isGlobal) {
      // Global users: filter from allAccounts based on school selection
      const baseAccounts = selectedSchool 
        ? allAccounts.filter(account => 
            account.accesscards.some(card => card.school_id === selectedSchool.school_id)
          )
        : allAccounts;
      
      const filteredAccounts = applyFilters(baseAccounts, selectedRole, associationId, selectedPeriod, selectedTOR);
      setAccounts(filteredAccounts);
    } else {
      // Non-global users: filter from allAccounts (which contains their school's data)
      const baseAccounts = allAccounts;
      const filteredAccounts = applyFilters(baseAccounts, selectedRole, associationId, selectedPeriod, selectedTOR);
      setAccounts(filteredAccounts);
    }
  };

  const handlePeriodFilter = (periodId) => {
    setSelectedPeriod(periodId);
    
    // For both global and non-global users, use the same filtering logic
    if (userPermissions?.isGlobal) {
      // Global users: filter from allAccounts based on school selection
      const baseAccounts = selectedSchool 
        ? allAccounts.filter(account => 
            account.accesscards.some(card => card.school_id === selectedSchool.school_id)
          )
        : allAccounts;
      
      const filteredAccounts = applyFilters(baseAccounts, selectedRole, selectedAssociation, periodId, selectedTOR);
      setAccounts(filteredAccounts);
    } else {
      // Non-global users: filter from allAccounts (which contains their school's data)
      const baseAccounts = allAccounts;
      const filteredAccounts = applyFilters(baseAccounts, selectedRole, selectedAssociation, periodId, selectedTOR);
      setAccounts(filteredAccounts);
    }
  };

  const handleTORFilter = (torFilter) => {
    setSelectedTOR(torFilter);
    
    // For both global and non-global users, use the same filtering logic
    if (userPermissions?.isGlobal) {
      // Global users: filter from allAccounts based on school selection
      const baseAccounts = selectedSchool 
        ? allAccounts.filter(account => 
            account.accesscards.some(card => card.school_id === selectedSchool.school_id)
          )
        : allAccounts;
      
      const filteredAccounts = applyFilters(baseAccounts, selectedRole, selectedAssociation, selectedPeriod, torFilter);
      setAccounts(filteredAccounts);
    } else {
      // Non-global users: filter from allAccounts (which contains their school's data)
      const baseAccounts = allAccounts;
      const filteredAccounts = applyFilters(baseAccounts, selectedRole, selectedAssociation, selectedPeriod, torFilter);
      setAccounts(filteredAccounts);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '48px 16px', textAlign: 'center', backgroundColor: '#f8fafc', minHeight: '50vh' }}>
        <LoadingSpinner message="Loading community…" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '24px 16px',
          margin: 16,
          maxWidth: 480,
          marginLeft: 'auto',
          marginRight: 'auto',
          textAlign: 'center',
          color: '#b91c1c',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          fontSize: '13px',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '16px',
        maxWidth: 1100,
        margin: '0 auto',
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '18px 20px',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
          border: '1px solid #e2e8f0',
        }}
      >
        <h1
          style={{
            margin: '0 0 4px 0',
            color: '#0f172a',
            fontSize: '22px',
            fontWeight: 600,
            letterSpacing: '-0.02em',
          }}
        >
          Community
        </h1>
        <p style={{ margin: '0 0 18px 0', color: '#64748b', fontSize: '13px', lineHeight: 1.45 }}>
          Directory for your organization. Select a scope, filter, then open a person for details.
        </p>

      {/* School Selection for Global Users */}
      {userPermissions?.isGlobal && schools.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <p style={uiSectionLabel}>Select school</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            <div
              role="button"
              tabIndex={0}
              onClick={handleShowAllSchools}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleShowAllSchools();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                backgroundColor: !selectedSchool ? '#f1f5f9' : '#ffffff',
                border: `1px solid ${!selectedSchool ? '#cbd5e1' : '#e2e8f0'}`,
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: !selectedSchool ? '#e2e8f0' : '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#64748b',
                }}
              >
                All
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ margin: 0, color: '#0f172a', fontSize: '13px', fontWeight: 600, lineHeight: 1.3 }}>
                  All schools
                </div>
                <div style={{ margin: '2px 0 0 0', color: '#94a3b8', fontSize: '11px' }}>Cross-school list</div>
              </div>
            </div>

            {schools.map((school) => {
              const on = selectedSchool?.school_id === school.school_id;
              return (
                <div
                  key={school.school_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSchoolSelect(school)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSchoolSelect(school);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    backgroundColor: on ? '#f1f5f9' : '#ffffff',
                    border: `1px solid ${on ? '#cbd5e1' : '#e2e8f0'}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                  }}
                >
                  <img
                    src={
                      school.logo_url ||
                      'https://acuitycte-files.s3.us-west-2.amazonaws.com/public/acuity-tech-logo.png'
                    }
                    alt=""
                    style={{
                      width: 36,
                      height: 36,
                      objectFit: 'contain',
                      borderRadius: 6,
                      flexShrink: 0,
                      backgroundColor: 'white',
                      padding: 2,
                      border: '1px solid #e2e8f0',
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        margin: 0,
                        color: '#0f172a',
                        fontSize: '13px',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {school.school_name}
                    </div>
                    <div style={{ margin: '2px 0 0 0', color: '#94a3b8', fontSize: '11px' }}>School scope</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Role Filter */}
      <div style={{ marginBottom: 14 }}>
        <p style={uiSectionLabel}>Role</p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => handleRoleFilter('All Roles')} style={uiFilterChip(selectedRole === 'All Roles')}>
            All roles
          </button>
          {ROLES.map((role) => (
            <RoleTooltip key={role} content={ROLE_DESCRIPTIONS[role]}>
              <button type="button" onClick={() => handleRoleFilter(role)} style={uiFilterChip(selectedRole === role)}>
                {role}
              </button>
            </RoleTooltip>
          ))}
        </div>
      </div>

      {selectedRole === 'Student' && studentPeriods.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <p style={uiSectionLabel}>Term</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => handlePeriodFilter('All Terms')} style={uiFilterChip(selectedPeriod === 'All Terms')}>
              All terms
            </button>
            {studentPeriods.map((period) => (
              <button
                key={period.period_id}
                type="button"
                onClick={() => handlePeriodFilter(period.period_id)}
                style={uiFilterChip(selectedPeriod === period.period_id)}
              >
                {period.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedRole === 'Student' && studentAssociations.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <p style={uiSectionLabel}>Association</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => handleAssociationFilter('All Associations')}
              style={uiFilterChip(selectedAssociation === 'All Associations')}
            >
              All associations
            </button>
            {studentAssociations.map((association) => (
              <button
                key={association.id}
                type="button"
                onClick={() => handleAssociationFilter(association.id)}
                style={uiFilterChip(selectedAssociation === association.id)}
              >
                {association.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedRole === 'Instructor' && (
        <div style={{ marginBottom: 14 }}>
          <p style={uiSectionLabel}>Instructor type</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => handleTORFilter('All TORs')} style={uiFilterChip(selectedTOR === 'All TORs')}>
              All instructors
            </button>
            <button type="button" onClick={() => handleTORFilter('Regular')} style={uiFilterChip(selectedTOR === 'Regular')}>
              Regular
            </button>
            <button type="button" onClick={() => handleTORFilter('TOR')} style={uiFilterChip(selectedTOR === 'TOR')}>
              Teachers of record
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          marginBottom: 14,
          padding: '10px 12px',
          backgroundColor: '#f8fafc',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', lineHeight: 1.35 }}>
          {userPermissions?.isGlobal && !selectedSchool
            ? 'All schools'
            : selectedSchool?.school_name || userPermissions?.school_name || 'Your school'}
        </div>
        <div style={{ marginTop: 4, fontSize: '12px', color: '#64748b' }}>
          {accounts.length} user{accounts.length !== 1 ? 's' : ''}
          {userPermissions?.isGlobal && !selectedSchool ? ' · all schools' : ''}
        </div>
      </div>

      {accounts.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '28px 16px',
            color: '#64748b',
            backgroundColor: '#f8fafc',
            borderRadius: 8,
            border: '1px dashed #cbd5e1',
            fontSize: '13px',
          }}
        >
          <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>No users match</div>
          <div>Try another role, term, association, or school.</div>
        </div>
      ) : (
        <>
          <p style={{ ...uiSectionLabel, marginBottom: 8 }}>People</p>
          <div style={{ display: 'grid', gap: 8 }}>
            {accounts.map((account) => (
              <AccountCard
                key={account.account_id}
                account={account}
                torStudents={torStudents}
                canAddStudentEnrollment={communityCapabilities.AddStudentEnrollment}
                onAddEnrollment={
                  communityCapabilities.AddStudentEnrollment
                    ? (card) => {
                        setSelectedAccessCard(card);
                        setIsAddEnrollmentModalOpen(true);
                      }
                    : undefined
                }
              />
            ))}
          </div>
        </>
      )}
      </div>

      {communityCapabilities.CreateUser ? (
        <button
          type="button"
          aria-label="Create new user"
          title="Create new user"
          onClick={() => setIsCreateUserModalOpen(true)}
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: '#334155',
            color: '#fff',
            border: '1px solid #1e293b',
            cursor: 'pointer',
            fontSize: '22px',
            fontWeight: 500,
            lineHeight: 1,
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.2)',
            zIndex: 99,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1e293b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#334155';
          }}
        >
          +
        </button>
      ) : null}

      <CreateUserModal
        isOpen={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
        schools={schools}
        userPermissions={userPermissions}
        periods={studentPeriods}
        associations={studentAssociations}
        concentrations={concentrations}
        canAddStudentEnrollment={communityCapabilities.AddStudentEnrollment}
      />

      <AddEnrollmentModal
        isOpen={isAddEnrollmentModalOpen}
        onClose={() => {
          setIsAddEnrollmentModalOpen(false);
          setSelectedAccessCard(null);
        }}
        accesscard={selectedAccessCard}
        periods={studentPeriods}
        associations={studentAssociations}
        concentrations={concentrations}
        onEnrollmentAdded={() => {
          fetchCommunityData();
        }}
      />
    </div>
  );
};

function AccountCard({ account, torStudents = {}, onAddEnrollment, canAddStudentEnrollment = false }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCardClick = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: '10px 12px',
          backgroundColor: '#fafafa',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f1f5f9';
          e.currentTarget.style.borderColor = '#cbd5e1';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#fafafa';
          e.currentTarget.style.borderColor = '#e2e8f0';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 40,
              height: 40,
              backgroundColor: '#f8fafc',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: '1px solid #e2e8f0',
              fontSize: '14px',
              fontWeight: 600,
              color: '#64748b',
            }}
          >
            {account.first_name?.[0]}
            {account.last_name?.[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ margin: 0, color: '#0f172a', fontSize: '14px', fontWeight: 600, lineHeight: 1.3 }}>
              {account.first_name} {account.last_name}
            </div>
            <div style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '12px', wordBreak: 'break-word' }}>
              {account.primary_email}
            </div>
            {account.dob && (
              <div style={{ margin: '2px 0 0 0', color: '#94a3b8', fontSize: '11px' }}>
                DOB {new Date(account.dob).toLocaleDateString()}
              </div>
            )}
          </div>
          <div style={{ color: '#cbd5e1', fontSize: '16px', flexShrink: 0 }} aria-hidden>
            ›
          </div>
        </div>
      </div>

      {/* Modal */}
      <AccountDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        account={account}
        torStudents={torStudents}
        onAddEnrollment={onAddEnrollment}
        canAddStudentEnrollment={canAddStudentEnrollment}
      />
    </>
  );
}

function AccessCardDisplay({ card, onAddEnrollment, canAddStudentEnrollment = false }) {
  return (
    <div style={{
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '12px 14px',
      marginBottom: '10px',
      backgroundColor: '#fafafa',
      transition: 'background-color 0.15s ease, border-color 0.15s ease'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#f1f5f9';
      e.currentTarget.style.borderColor = '#cbd5e1';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = '#fafafa';
      e.currentTarget.style.borderColor = '#e2e8f0';
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: card.enrollments?.length ? '10px' : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <img
            src={card.school_logo || "https://acuitycte-files.s3.us-west-2.amazonaws.com/public/acuity-tech-logo.png"}
            alt=""
            style={{ 
              height: '36px', 
              width: '36px', 
              objectFit: 'contain', 
              borderRadius: '6px',
              backgroundColor: 'white',
              padding: '3px',
              border: '1px solid #e2e8f0',
              flexShrink: 0,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <h5 style={{ 
              margin: 0, 
              color: '#0f172a',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: 1.3,
            }}>
              {card.school_name || 'Global'} · {card.role}
            </h5>
            <p style={{ 
              margin: '2px 0 0 0', 
              color: '#64748b', 
              fontSize: '12px',
              wordBreak: 'break-word',
            }}>
              {card.email}
            </p>
          </div>
        </div>
      </div>

      {/* Enrollments Section */}
      {card.enrollments && card.enrollments.length > 0 && (
        <div style={{ marginTop: '2px' }}>
          <div
            style={{
              marginBottom: '8px',
              color: '#475569',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <span>Enrollments · {card.enrollments.length}</span>
            {card.role === 'Student' && canAddStudentEnrollment && onAddEnrollment ? (
              <button
                type="button"
                onClick={() => onAddEnrollment(card)}
                style={{
                  padding: '4px 10px',
                  backgroundColor: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
              >
                + Add
              </button>
            ) : null}
          </div>
          {card.enrollments.map((enrollment, idx) => (
            <div 
              key={enrollment.enrollment_id || idx}
              style={{
                padding: '10px 12px',
                backgroundColor: '#ffffff',
                borderRadius: '6px',
                marginBottom: '6px',
                border: '1px solid #e2e8f0',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#0f172a',
                    fontWeight: 500,
                    marginBottom: '6px',
                    lineHeight: 1.4,
                  }}>
                    {dateTimeToMonDDYYYYWithTime(enrollment.start_date,false)}
                    {enrollment.end_date && (
                      <span style={{ marginLeft: '8px', color: '#64748b', fontWeight: 400 }}>
                        → {dateTimeToMonDDYYYYWithTime(enrollment.end_date,false)}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {enrollment.gradelevel && (
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#64748b',
                        backgroundColor: '#f8fafc',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                      }}>
                        Grade {enrollment.gradelevel}
                      </span>
                    )}
                    {enrollment.concentration_title && (
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#64748b',
                        backgroundColor: '#f8fafc',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                      }}>
                        {enrollment.concentration_title}
                      </span>
                    )}
                    {enrollment.association_title && (
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#64748b',
                        backgroundColor: '#f8fafc',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                      }}>
                        {enrollment.association_title}
                      </span>
                    )}
                    {enrollment.period_name && (
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#64748b',
                        backgroundColor: '#f8fafc',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                      }}>
                        {enrollment.period_name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Community;
